import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isEmpty from '../../../../is-empty';
import base64 from 'base-64';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
// import { pdfjs } from 'react-pdf';
import { Dropzone, FileMosaic } from "@files-ui/react";
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/DeleteTwoTone';
import DownloadIcon from '@mui/icons-material/DownloadTwoTone';
import ToolTip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import TableFooter from '@mui/material/TableFooter';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Confirmation from '../../Dialog/Confirmation';
import ModalDialog from '../../Dialog/ModalDialog';
import DateDisplay from '../../DateDisplay/DateDisplay';
import Link from '../../Link/Link';
import { EvoraTabs, TabCanvas } from '../../Tabs/Tabs';
import { UploadButton } from '../Buttons';
import common from '../../../../jsons/common.json';
import AttachmentViewer from './AttachmentViewer';
import { ModalToaster } from '../../Toaster/Toaster';
import EditIcon from '@mui/icons-material/EditTwoTone';
import TextField from '../../TextField/TextField';
import CheckIcon from '@mui/icons-material/CheckRounded';
import ClearIcon from '@mui/icons-material/ClearRounded';
import PrintIcon from 'mdi-material-ui/PrinterPosPlus';
import EyeIcon from 'mdi-material-ui/Eye';
import { isPersistedId } from '../../../../helpers/idHelper';

// /api/document/list populates Document.uploadedBy with firstName/lastName.
// Rows predating the provenance backfill — and rows whose source `user` was
// itself null (system-generated documents) — carry no uploader, so fall back to
// blank rather than rendering an ObjectId.
const uploaderName = user => {
    if(user == null || typeof user !== 'object')
        return '';
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
}

// A controlled document is generated into the final documents tab, so the badge
// belongs beside the file name here — this dialog, not File Manager, is where
// people actually take the file. Every download of one is logged against the
// record, and only the first claims the master; the rest expire. The tooltip
// carries that detail rather than adding two more columns to an already wide
// table. Nothing renders for ordinary documents.
const MasterCopyBadge = ({ row, t, dateFormat }) => {
    if(row.masterCopy !== true)
        return null;
    const stamp = dateFormat + ' hh:mm a';
    const title = (
        <>
            <div>{`${t('generatedBy')}: ${uploaderName(row.masterCopyGeneratedBy) || '-'}`}</div>
            <div><DateDisplay value={row.masterCopyGeneratedOn} format={stamp} /></div>
            <div>
                {row.masterCopyClaimedOn != null ?
                    `${t('master')}: ${uploaderName(row.masterCopyClaimedBy) || '-'}` :
                    `${t('master')}: ${t('masterNotIssued')}`}
            </div>
            {row.masterCopyClaimedOn != null &&
                <div><DateDisplay value={row.masterCopyClaimedOn} format={stamp} /></div>
            }
        </>
    );
    return (
        <ToolTip title={title} placement='top-start'>
            <Box component='span' sx={{
                ml: 1, px: 0.5, borderRadius: 0.5, whiteSpace: 'nowrap', fontSize: '0.6rem',
                border: 1, borderColor: 'error.main', color: 'error.main'
            }}>
                {t('masterCopy')}
            </Box>
        </ToolTip>
    );
}

export default function Attachments(props) {
    const { finalDocuments, objectType, recordId, toggleDialog, open, reloadParent } = props;
    const { t } = useTranslation('translation');
    const changed = useRef(false);
    const theme = useTheme();
    const [openViewer, setOpenViewer] = useState(false);
    const [viewing, setViewing] = useState({});
    const [dataFetched, setDataFetched] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState({
        status: false,
        type: ''
    });
    const [index, setIndex] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [uploads, setUploads] = useState([]);
    const [tabKey, setTabKey] = useState(0);
    const [systemMessage, setSystemMessage] = useState(null);
    const [tableControls, setTableControls] = useState({
        totalCount: 0,
        page: 0,
        offset: 0,
        pageCount: 1,
        size: 5,
        documentCount: 0
    });
    const [editAttachment, setEditAttachment] = useState(null);
    const [editDocument, setEditDocument] = useState(null);
    const [newFileName, setNewFileName] = useState('');
    const [errors, setErrors] = useState({});
    const dateFormat = useSelector(state => state.auth.user.dateFormat);
    const security = useSelector(state => state.security.currentPath);
    const tabLabels = [
        { name: t('finalDocuments'), disabled: false, count: tableControls.documentCount },
        { name: t('attachments'), disabled: false, count: tableControls.totalCount },
        { name: t('upload'), disabled: !security.documentUpload, count: uploads.length, countColor: 'secondary' }
    ]

    const finalDocumentsKey = useMemo(() => JSON.stringify(finalDocuments ?? []), [finalDocuments]);
    const { size, offset } = tableControls;

    const apiCall = useCallback(async (signal) => {
        try {
            const data = {
                size,
                offset,
                finalDocuments: JSON.parse(finalDocumentsKey)
            }
            const attachmentList = await axios.post('/api/document/list/' + objectType + '/' + recordId, data, { signal });
            setAttachments(attachmentList.data.attachments);
            setDocuments(attachmentList.data.documents);
            setTableControls(prevControls => ({
                ...prevControls,
                totalCount: attachmentList.data.attachmentCount,
                documentCount: attachmentList.data.documentCount
            }));
            setDataFetched(true);
        } catch(err) {
            if(!axios.isCancel(err))
                console.error(err);
        }
    }, [finalDocumentsKey, objectType, recordId, size, offset]);

    useEffect(() => {
        // Tour mocks (and unsaved records) carry ids that aren't ObjectIds; the
        // list endpoint casts both the record id and the finalDocuments ids, so
        // fetching for one only produces a 500.
        if (dataFetched || !isPersistedId(recordId))
            return;
        const controller = new AbortController();
        apiCall(controller.signal);
        return () => controller.abort();
    }, [apiCall, dataFetched, recordId]);

    const handleOpenViewer = record => {
        setOpenViewer(true);
        setViewing(record);
    }

    const handleDownload = async (record) => {
        // Bundled same-origin instead of fetched from unpkg.com - CSP's worker-src only
        // allows 'self'/blob:, and a cross-origin CDN worker is also a production
        // reliability risk this security-hardened app shouldn't depend on regardless.
        // pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        //     'pdfjs-dist/build/pdf.worker.min.mjs',
        //     import.meta.url,
        //   ).toString();
        let data = await axios.get('/api/document/get/' + record._id + '?download=true');
        let link = document.createElement("a");
        link.download = record.name;
        link.href = 'data:application/octet-stream;base64,' + data.data;
        link.click();
    }

    const changeTab = (event, newValue) => {
        if(tabKey === 1) {
            setTableControls({
                ...tableControls, 
                page: 0, 
                offset: 0
            });
            setDataFetched(false);
            if(editDocument != null)
                setEditDocument(null);
        }else if(tabKey === 2) {
            if(editAttachment != null)
                setEditAttachment(null);
        }
        setTabKey(newValue);
    };

    const toggleConfirm = (type, i) => {
        setIndex(i != null ? i : '');
        setConfirmDelete({ status: !confirmDelete.status, type: type });
    }

    const confirmClick = (answer) => async () => {
        if(answer === 'yes'){
            let doc = {};
            if(confirmDelete.type === 'documents')
                doc = documents[index];
            else if(confirmDelete.type === 'attachments')
                doc = attachments[index];
            if(!isEmpty(doc)){
                let data = {
                    objectType: objectType,
                    recordId: recordId,
                    _id: doc._id,
                    property: props.propName
                };
                try {
                    let result = await axios.post('/api/document/delete', data)
                    if(result.status === 200) {
                        changed.current = true;
                        setSystemMessage({
                            message: result.data.message,
                            title: t('success'),
                            severity: 'success',
                            onClose: () => setSystemMessage(null)
                        });
                        setDataFetched(false);
                    }
                } catch(err){
                    setSystemMessage({ 
                        message: err.response.data, 
                        title: t('error'), 
                        severity: 'error',
                        onClose: () => setSystemMessage(null)
                    });
                }
            }else{
                setSystemMessage({ 
                    message: t('noAttachmentSelected'), 
                    title: t('error'), 
                    severity: 'error',
                    onClose: () => setSystemMessage(null)
                });
            }            
        }
        setConfirmDelete({ status: !confirmDelete, type: '' });
    }

    const handleFileUpload = async () => {
        // Nothing can be attached to a record the server can't resolve — an
        // unsaved record, or the simulated one a training tour puts on screen.
        if(!isPersistedId(recordId)){
            setSystemMessage({
                message: t('saveRecordBeforeAttaching', 'Save this record before adding attachments.'),
                title: t('error'),
                severity: 'error',
                onClose: () => setSystemMessage(null)
            });
            return;
        }
        if(!uploading){
            setUploading(true);
            await _handleFileUpload();
        }
    }   

    const _handleFileUpload = async () => {
        try {
            let body = {count: uploads.length};
            let fileReadPromises = uploads.map((upload, index) => {
                return new Promise((resolve, reject) => {
                    let reader = new FileReader();
                    reader.onload = () => {
                        body['file' + index] = base64.encode(reader.result);
                        body['fileName' + index] = upload.name;
                        body['type' + index] = upload.type;
                        body['objectId' + index] = recordId;
                        body['objectType' + index] = objectType;
                        if(props.row != null)
                            body['row' + index] = props.row;
                        if(props.propName != null)
                            body['property' + index] = props.propName;
                        resolve();
                    };
                    reader.onerror = reject;
                    reader.readAsBinaryString(upload.file);
                });
            });    
            await Promise.all(fileReadPromises);
            let result = await axios.post('/api/document/attach', body);
            if(result.status === 200) {
                changed.current = true;
                setSystemMessage({
                    message: result.data.message,
                    title: t('success'),
                    severity: 'success',
                    onClose: () => setSystemMessage(null)
                });
                setDataFetched(false);
                setTabKey(1);
                setUploads([]);
            }
        } catch(err){
            setSystemMessage({ 
                message: err.response.data, 
                title: t('error'), 
                severity: 'error',
                onClose: () => setSystemMessage(null)
            });
        }
        setUploading(false);
    }

    const removeFile = (id) => {
        setUploads(uploads.filter((x) => x.id !== id));
    };

    const handleDrop = (incommingFiles) => {
        setUploads(incommingFiles);
    };

    const handleChangePage = (event, newPage) => {
        setTableControls({
            ...tableControls, 
            page: newPage, 
            offset: newPage * tableControls.size
        });
        setDataFetched(false);
    }

    const handleRowsPerPage = e => {
        setTableControls({
            ...tableControls, 
            size: parseInt(e.target.value)
        });
        setDataFetched(false);
    }

    const handleClose = () => {
        if(changed.current && reloadParent != null)
            reloadParent();
        toggleDialog();
    }

    const buttonStack = () => {
        let buttons = [];
        if(tabKey !== 2){
            buttons = [
                <Button key='cancelButton' variant="text" size='small' color="secondary" onClick={handleClose}>{t('close')}</Button>,
            ]
        }else{
            buttons = [
                <Button key='cancelButton' variant="text" size='small' color="secondary" onClick={handleClose}>{t('close')}</Button>,
                <UploadButton key='uploadButton' uploadClick={() => handleFileUpload()} enabled={!uploading}/>
            ]
        };
        return buttons;
    }

    const changeValue = (e) => {
        setNewFileName(e.target.value);
    }

    const saveFileName = async (i, type) => {
        if(newFileName === ''){
            setErrors({fileName: t('required')});
        }else{
            let originalName = type === 'documents' ? documents[i].name : attachments[i].name;
            let originalExtension = originalName.substring(originalName.lastIndexOf('.') + 1);
            let newExtension = newFileName.substring(newFileName.lastIndexOf('.') + 1);
            if(originalExtension !== newExtension){
                setErrors({fileName: t('extensionMismatch')});
                return;
            }
            let data = {
                _id: type === 'documents' ? documents[i]._id : attachments[i]._id,
                name: newFileName
            };
            try {
                let result = await axios.post('/api/document/updatefilename', data)
                if(result.status === 200) {
                    changed.current = true;
                    setSystemMessage({
                        message: result.data.message,
                        title: t('success'),
                        severity: 'success',
                        onClose: () => setSystemMessage(null)
                    });
                    setNewFileName('');
                    if(type === 'documents')
                        setEditDocument(null);
                    else
                        setEditAttachment(null);
                    setDataFetched(false);
                }
            } catch(err){
                setSystemMessage({ 
                    message: err.response.data, 
                    title: t('error'), 
                    severity: 'error',
                    onClose: () => setSystemMessage(null)
                });
            }
        }
    }

    const clearFileName = () => {
        if(newFileName !== '')
            setNewFileName('');
        if(editDocument != null)
            setEditDocument(null);
        else
            setEditAttachment(null);
    }

    const editFileName = (row, type) => {
        if(type === 'documents'){
            setNewFileName(documents[row].name);
            setEditDocument(row);
        }else{
            setNewFileName(attachments[row].name);
            setEditAttachment(row);
        }
    }

    const togglePartnerVisible = async (i) => {
        try {
            let result = await axios.post('/api/document/partnervisible/' + attachments[i]._id, {visibleToPartner: !attachments[i].visibleToPartner});
            if(result.status === 200) {
                changed.current = true;
                setSystemMessage({
                    message: result.data.message,
                    title: t('success'),
                    severity: 'success',
                    onClose: () => setSystemMessage(null)
                });
                setAttachments(prev => prev.map((row, idx) =>
                    idx === i ? { ...row, visibleToPartner: !row.visibleToPartner } : row
                ));
            }
        } catch(err){
            console.error(err);
        }
    };

    const printDocument = async (i) => {
        try {
            let result = await axios.post('/api/document/print/' + attachments[i]._id, {print: !attachments[i].print});
            if(result.status === 200) {
                changed.current = true;
                setSystemMessage({
                    message: result.data.message,
                    title: t('success'),
                    severity: 'success',
                    onClose: () => setSystemMessage(null)
                });
                setAttachments(prev => prev.map((row, idx) =>
                    idx === i ? { ...row, print: !row.print } : row
                ));
            }
        } catch(err){
            console.error(err);
        }
    }
    
    return (
        <>
            <ModalDialog buttonStack={buttonStack()} title={t('attachments')} toggleDialog={() => handleClose()}
                        dialogStatus={open} fullWidth maxWidth='md'>
                <EvoraTabs tabLabels={tabLabels} tabKey={tabKey} changeTab={changeTab} />
                <TabCanvas value={tabKey} index={0}>
                    <Grid container spacing={3} sx={{ minHeight: '450px' }}>
                        <Grid size={12}>
                            <TableContainer>
                                <Table size='small'>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: 44 }} />
                                            <TableCell sx={{ width: '100%', whiteSpace: 'nowrap' }}>{t('name')}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('uploadedBy')}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('lastUpdated')}</TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {documents?.length > 0 ?
                                            documents.map((row, i) => (
                                                <TableRow 
                                                    hover
                                                    key={i}
                                                    >
                                                    <TableCell sx={{ width: '10px' }}>
                                                        <ToolTip title={row.type} placement='right-start'>
                                                            <div>
                                                                {row.type === 'application/pdf' ?
                                                                    <FontAwesomeIcon icon={["far", "file-pdf"]} size='lg' color={theme.palette.primary.main} /> :
                                                                row.type === 'image/png' ?
                                                                    <FontAwesomeIcon icon={["far", "file-image"]} size='lg' color={theme.palette.secondary.main} /> :
                                                                    <FontAwesomeIcon icon={["far", "file"]} size='lg' color={theme.palette.secondary.main} />
                                                                }
                                                            </div>
                                                        </ToolTip>                                                    
                                                    </TableCell>
                                                    <TableCell sx={{ width: '100%' }}>
                                                        {editDocument === null || editDocument !== i ?
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <ToolTip title={row.name} placement='top-start'>
                                                                    <Box sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        <Link onClick={() => handleOpenViewer(row)} name={'openAttachment' + i}>
                                                                            {row.name}
                                                                        </Link>
                                                                    </Box>
                                                                </ToolTip>
                                                                <MasterCopyBadge row={row} t={t} dateFormat={dateFormat} />
                                                            </Box> :
                                                            <>
                                                                <TextField value={newFileName} onChange={(event) => changeValue(event, 'documents')} name="fileName"
                                                                size="medium" label={t('fileName')} required sx={{ width: '375px' }}
                                                                error={errors[t('fileName')] != null ? true : false} helperText={errors[t('fileName')]}/>
                                                                <IconButton 
                                                                    id={'saveFileName' + i}
                                                                    name={'saveFileName' + i}
                                                                    onClick={() => saveFileName(i, 'documents')}
                                                                    size='small'
                                                            sx={{ display: 'inline-flex', p: 0.5 }} >
                                                                        <CheckIcon fontSize='small' />
                                                                </IconButton>
                                                                <IconButton 
                                                                    id={'clearFileName' + i}
                                                                    name={'clearFileName' + i}
                                                                    onClick={() => clearFileName()}
                                                                    size='small'
                                                            sx={{ display: 'inline-flex', p: 0.5 }} >
                                                                        <ClearIcon fontSize='small' />
                                                                </IconButton>
                                                            </>
                                                        }                                                
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                        {uploaderName(row.uploadedBy)}
                                                        <Box sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                                            <DateDisplay value={row.uploadedOn} format={dateFormat + ' hh:mm a'}/>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                        <DateDisplay value={row.lastUpdated} format={dateFormat + ' hh:mm a'}/>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                        {security.update && isEmpty(editDocument) &&
                                                            <IconButton 
                                                                id={'editDocument' + i}
                                                                name={'editDocument' + i}
                                                                onClick={() => editFileName(i, 'documents')}
                                                                size='small'
                                                            sx={{ display: 'inline-flex', p: 0.5 }} >
                                                                    <EditIcon fontSize='small' />
                                                            </IconButton>
                                                        }
                                                        <IconButton
                                                            id={'downloadAttachment' + i}
                                                            name={'downloadAttachment' + i}
                                                            onClick={() => handleDownload(row)}
                                                            size='small' sx={{ display: 'inline-flex', p: 0.5 }}>
                                                                <DownloadIcon fontSize='small' />
                                                        </IconButton>
                                                        {security.documentDelete &&
                                                            <IconButton
                                                                id={'deleteAttachment' + i}
                                                                name={'deleteAttachment' + i}
                                                                onClick={() => toggleConfirm('documents', i)}
                                                                size='small' sx={{ display: 'inline-flex', p: 0.5 }}>
                                                                    <DeleteIcon fontSize='small' />
                                                            </IconButton>
                                                        }
                                                    </TableCell>
                                                </TableRow>
                                            )) :
                                            <TableRow>
                                                <TableCell colSpan={5} sx={{ textAlign: 'center' }}>{t('noFinalDocuments')}</TableCell>
                                            </TableRow>
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </TabCanvas>
                <TabCanvas value={tabKey} index={1}>
                    <Grid container spacing={3} sx={{ minHeight: '450px' }}>
                        <Grid size={12}>
                            <TableContainer>
                                <Table size='small'>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: 44 }} />
                                            <TableCell sx={{ width: '100%', whiteSpace: 'nowrap' }}>{t('name')}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('uploadedBy')}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('lastUpdated')}</TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {attachments?.length > 0 ?
                                            attachments.map((row, i) => (
                                                <TableRow 
                                                    hover
                                                    key={i}
                                                    >
                                                    <TableCell sx={{ width: '10px' }}>
                                                        <ToolTip title={row.type} placement='right-start'>
                                                            <div>
                                                                {row.type === 'application/pdf' ?
                                                                    <FontAwesomeIcon icon={["far", "file-pdf"]} size='lg' color={row.isGenerated ? theme.palette.primary.main : theme.palette.secondary.main} /> :
                                                                row.type === 'image/png' ?
                                                                    <FontAwesomeIcon icon={["far", "file-image"]} size='lg' color={theme.palette.secondary.main} /> :
                                                                    <FontAwesomeIcon icon={["far", "file"]} size='lg' color={theme.palette.secondary.main} />
                                                                }
                                                            </div>
                                                        </ToolTip>                                                    
                                                    </TableCell>
                                                    <TableCell sx={{ width: '100%' }}>
                                                        {editAttachment === null || editAttachment !== i ?
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <ToolTip title={row.name} placement='top-start'>
                                                                    <Box sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        <Link onClick={() => handleOpenViewer(row)} name={'openAttachment' + i}>
                                                                            {row.name}
                                                                        </Link>
                                                                    </Box>
                                                                </ToolTip>
                                                                <MasterCopyBadge row={row} t={t} dateFormat={dateFormat} />
                                                            </Box> :
                                                            <>
                                                                <TextField value={newFileName} onChange={(event) => changeValue(event, 'attachments')} name="fileName"
                                                                size="medium" label={t('fileName')} required sx={{ width: '375px' }}
                                                                error={errors[t('fileName')] != null ? true : false} helperText={errors[t('fileName')]}/>
                                                                <IconButton 
                                                                    id={'saveFileName' + i}
                                                                    name={'saveFileName' + i}
                                                                    onClick={() => saveFileName(i, 'attachments')}
                                                                    size='small'
                                                            sx={{ display: 'inline-flex', p: 0.5 }} >
                                                                        <CheckIcon fontSize='small' />
                                                                </IconButton>
                                                                <IconButton 
                                                                    id={'clearFileName' + i}
                                                                    name={'clearFileName' + i}
                                                                    onClick={() => clearFileName()}
                                                                    size='small'
                                                            sx={{ display: 'inline-flex', p: 0.5 }} >
                                                                        <ClearIcon fontSize='small' />
                                                                </IconButton>
                                                            </>
                                                        }
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                        {uploaderName(row.uploadedBy)}
                                                        <Box sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                                            <DateDisplay value={row.uploadedOn} format={dateFormat + ' hh:mm a'}/>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                        <DateDisplay value={row.lastUpdated} format={dateFormat + ' hh:mm a'}/>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                        {security.update && isEmpty(editAttachment) &&
                                                            <IconButton 
                                                                id={'editAttachment' + i}
                                                                name={'editAttachment' + i}
                                                                onClick={() => editFileName(i, 'attachments')}
                                                                size='small'
                                                            sx={{ display: 'inline-flex', p: 0.5 }} >
                                                                    <EditIcon fontSize='small' />
                                                            </IconButton>
                                                        }
                                                        <IconButton
                                                            id={'downloadAttachment' + i}
                                                            name={'downloadAttachment' + i}
                                                            onClick={() => handleDownload(row)}
                                                            size='small' sx={{ display: 'inline-flex', p: 0.5 }}>
                                                                <DownloadIcon fontSize='small' />
                                                        </IconButton>
                                                        <IconButton
                                                            id={'printDocument' + i}
                                                            name={'printDocument' + i}
                                                            disabled={row.type === 'application/pdf'}
                                                            onClick={() => printDocument(i)}
                                                            size='small'
                                                            sx={{ display: 'inline-flex', p: 0.5 }} >
                                                                <PrintIcon fontSize='small' color={row.type === 'application/pdf' ? 'disabled' :
                                                                                                    row.print ? 'primary' : 'none'}/>
                                                        </IconButton>
                                                        <IconButton
                                                            id={'partnerVisible' + i}
                                                            name={'partnerVisible' + i}
                                                            title='Visible to partner portal'
                                                            onClick={() => togglePartnerVisible(i)}
                                                            size='small'
                                                            sx={{ display: 'inline-flex', p: 0.5 }} >
                                                                <EyeIcon fontSize='small' color={row.visibleToPartner ? 'primary' : 'disabled'}/>
                                                        </IconButton>
                                                        <IconButton
                                                            id='deleteAttachment'
                                                            name='deleteAttachment'
                                                            onClick={() => toggleConfirm('attachments', i)}
                                                            size='small' sx={{ display: 'inline-flex', p: 0.5 }}>
                                                                <DeleteIcon fontSize='small' />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            )) :
                                            <TableRow>
                                                <TableCell colSpan={5} sx={{ textAlign: 'center' }}>{t('noAttachments')}</TableCell>
                                            </TableRow>
                                        }
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow key="tableFooter">
                                            <TablePagination count={tableControls.totalCount}
                                            rowsPerPage={tableControls.size}
                                            onPageChange={handleChangePage}
                                            page = {tableControls.page}
                                            rowsPerPageOptions={common.rowOptions}
                                            onRowsPerPageChange={handleRowsPerPage}/>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </TabCanvas>
                <TabCanvas value={tabKey} index={2}>
                    <Grid container spacing={3} sx={{ minHeight: '450px' }}>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12
                            }}>
                            <Dropzone onChange={handleDrop} value={uploads} minHeight='400px'>
                                {uploads.map((file, i) => (
                                    <FileMosaic 
                                        {...file} 
                                        key={'uploadMosiac'+i} 
                                        preview 
                                        onDelete={removeFile} 
                                        info
                                    />
                                ))}
                            </Dropzone>
                        </Grid>
                    </Grid>
                </TabCanvas>
                <ModalToaster alert={systemMessage} />
            </ModalDialog>
            {confirmDelete.status && 
                <Confirmation
                    open={confirmDelete.status}
                    no={confirmClick('no')}
                    noButton={t('no')}
                    yes={confirmClick('yes')}
                    yesButton={t('yes')}
                    title={t('areYouSure')}
                    content={t('deleteDocumentMessage')}
                    toggleDialog={() => setConfirmDelete({
                        status: false,
                        type: ''
                    })}
                />
            }
            {openViewer &&
                <AttachmentViewer
                    open={openViewer}
                    toggleDialog={() => setOpenViewer(!openViewer)}
                    attachment={viewing}
                />
            }
        </>
    );
}